import type { PortalNotificationFeedResponse } from '@/lib/api/client-portal.api'
import type { PortalNotificationContext } from '@/modules/notifications/portal-notifications.api'
import {
  executeNotificationReader,
  isNotificationReaderRefused,
  useNotificationReaderRefused,
} from '@/hooks/queries/notification-reader-cache'
import { useOwnedReaderMutation } from '@/hooks/queries/useOwnedReaderMutation'
import { usePortalNotificationSession } from '@/hooks/queries/usePortalNotificationSession'
import { portalDirectoryKeys } from '@/modules/client-portal/directory.api'
import { portalMetadataKeys } from '@/modules/client-portal/metadata.api'
import { portalNotificationsApi } from '@/modules/notifications/portal-notifications.api'
import { isNotificationReadUnavailable } from '@/modules/notifications/reads.api'
import {
  hashKey,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

export const portalNotificationRootKey = (
  entityId: string | undefined | null,
) => ['client-portal', entityId, 'notifications']
const retry = (failures: number, error: unknown) =>
  !isNotificationReadUnavailable(error) && failures < 2

export function useClientPortalNotifications(
  entityId: string | undefined | null,
  limit = 20,
) {
  const session = usePortalNotificationSession(entityId)
  const client = useQueryClient()
  const key = [
    ...portalNotificationRootKey(entityId),
    'popover',
    limit,
    session.key,
  ]
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      executeNotificationReader(client, key, session, signal, context =>
        portalNotificationsApi.read(context, limit),
      ),
    enabled: session.enabled,
    staleTime: 15000,
    gcTime: 60000,
    retry,
  })
  const refused = useNotificationReaderRefused(client, key)
  return {
    ...query,
    unavailable: !session.enabled || query.isError || refused,
    readOnly: session.preview || query.data?.isInternalViewer === true,
    refetch: async (...args: Parameters<typeof query.refetch>) => {
      session.assertCurrent()
      const result = await query.refetch(...args)
      session.assertCurrent()
      return result
    },
    data:
      session.enabled && !isNotificationReadUnavailable(query.error) && !refused
        ? query.data
        : undefined,
  }
}

export function useClientPortalNotificationHistory(
  entityId: string | undefined | null,
  pageSize = 30,
) {
  const session = usePortalNotificationSession(entityId)
  const client = useQueryClient()
  const key = [
    ...portalNotificationRootKey(entityId),
    'history',
    pageSize,
    session.key,
  ]
  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ signal, pageParam }) =>
      executeNotificationReader(
        client,
        key,
        session,
        signal,
        context => portalNotificationsApi.read(context, pageSize, pageParam),
        false,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + pageSize : undefined,
    enabled: session.enabled,
    staleTime: 15000,
    gcTime: 60000,
    retry,
  })
  const refused = useNotificationReaderRefused(client, key)
  return {
    ...query,
    unavailable: !session.enabled || query.isError || refused,
    readOnly:
      session.preview ||
      query.data?.pages.some(page => page.isInternalViewer) === true,
    refetch: async (...args: Parameters<typeof query.refetch>) => {
      session.assertCurrent()
      const result = await query.refetch(...args)
      session.assertCurrent()
      return result
    },
    fetchNextPage: async (...args: Parameters<typeof query.fetchNextPage>) => {
      session.assertCurrent()
      const result = await query.fetchNextPage(...args)
      session.assertCurrent()
      return result
    },
    data:
      session.enabled && !isNotificationReadUnavailable(query.error) && !refused
        ? query.data
        : undefined,
  }
}

type Command = {
  action: 'read' | 'dismiss' | 'read-all'
  notificationId?: string
}
function usePortalNotificationCommand(
  entityId: string | null | undefined,
  action: Command['action'],
) {
  const session = usePortalNotificationSession(entityId)
  const client = useQueryClient()
  const root = portalNotificationRootKey(entityId)
  return useOwnedReaderMutation<
    string | void,
    { updated: number },
    PortalNotificationContext
  >(session, {
    queryRoot: root,
    singleFlight: true,
    execute: (notificationId, context) => {
      // Admission requires a current owned reader; legacy/manual cache bodies
      // cannot restore a denied feed. The server rechecks portal record policy.
      const readers = client
        .getQueryCache()
        .findAll({ queryKey: root })
        .filter(
          query =>
            hashKey([query.queryKey.at(-1)]) === hashKey([session.key]) &&
            query.state.status === 'success' &&
            !isNotificationReaderRefused(client, query.queryKey),
        )
      const pages = readers.flatMap(query => {
        const data = query.state.data as
          | PortalNotificationFeedResponse
          | { pages: PortalNotificationFeedResponse[] }
          | undefined
        return data ? ('pages' in data ? data.pages : [data]) : []
      })
      if (
        session.preview ||
        !pages.length ||
        pages.some(page => page.isInternalViewer) ||
        (action !== 'read-all' &&
          !pages.some(page =>
            page.items.some(item => item.id === notificationId),
          ))
      )
        throw new Error('Refresh portal notifications before trying again')
      return portalNotificationsApi.change(
        context,
        action,
        typeof notificationId === 'string' ? notificationId : undefined,
      )
    },
    extraReaders: () => [
      portalDirectoryKeys.list(session.key.workspaceSession),
      portalMetadataKeys.counts(entityId, {
        workspaceSession: session.key.workspaceSession,
        portalEntityId: session.key.portalEntityId,
      }),
    ],
    errorMessage:
      'The notification change could not be confirmed. Refresh before trying again.',
  })
}
export const useMarkPortalNotificationRead = (
  entityId: string | null | undefined,
) => usePortalNotificationCommand(entityId, 'read')
export const useDismissPortalNotification = (
  entityId: string | null | undefined,
) => usePortalNotificationCommand(entityId, 'dismiss')
export const useMarkAllPortalNotificationsRead = (
  entityId: string | null | undefined,
) => usePortalNotificationCommand(entityId, 'read-all')
